import {Injectable} from '@angular/core';
import {from, Observable, of} from 'rxjs';
import {HttpEvent, HttpEventType, HttpProgressEvent, HttpResponse} from '@angular/common/http';
import {CurrentSettings, State, StateInterface} from '../../interfaces/biozoom-api/app-configuration';
import {ScannerStatus} from '../../interfaces/scanner-api/scanner-status';
import {catchError, delay, repeat, switchMap, timeout} from 'rxjs/operators';
import * as uuid from 'uuid';
import {evalJsonWrapper, parseHandRecognition} from '../../methods/blockFormat';
import {PipelineStageConfig} from '../../interfaces/biozoom-api/pipeline';
import {getKeyVal} from '../../methods/helpers';

declare global {
  interface Window {
    /**
     * Electron API for serial communication and port management.
     */
    electronAPI: {
      listPorts: () => Promise<any[]>;
      openPort: ({path, baudRate}) => Promise<string>;
      closePort: () => Promise<void>;
      onSerialData: (callback: (data: any) => void) => void;
      setSignals: (state: {rts: boolean, dtr: boolean}) => Promise<string>;
      write: (data: {command: string}) => Promise<string>;
      writePayload: (data: {command: string, receivedData: any}) => Promise<string>;
      status: () => Promise<boolean>;
      isKioskMode: () => Promise<boolean>;
      isConfigCheckerEnabled: () => Promise<boolean>;
      screenshot: (data: {outputPath: string}) => Promise<{ success: boolean, outputPath?: string, error?: string }>;
      generatePDF: (data: {imagePaths: string[], outputPath: string}) => Promise<{ success: boolean, outputPath?: string, error?: string }>;
      exportPDF: (data: {sourcePath: string, defaultName: string}) => Promise<{ success: boolean, filePath?: string, cancelled?: boolean, error?: string }>;
    };
  }
}

const expectedKeys = [
  'host',
  'product',
  'serial',
  'sensor',
  'sensor_firmware',
  'sensor_temp',
  'ip',
  'battery_voltage',
  'usb_voltage',
  'usb_charging',
  'memory_free_64k_blocks',
  'memory_healthy',
  'wifi_firmware',
  'wifi_name',
  'wifi_strength',
  'uptime',
  'svn',
  'timestamp',
  'production',
  'meas_count_wifi',
  'capabilities',
  'capabilities.SUPPORT_SPI_MEMORY',
  'capabilities.SUPPORT_CHARGE_CONTROL',
  'capabilities.SUPPORT_IDLE_TIMER',
  'capabilities.SUPPORT_MDNS',
  'capabilities.SUPPORT_USB_LEGACY_MODE',
  'capabilities.WITH_CHARGE_LED',
  'capabilities.AP_PASS',
  'capabilities.CORE_DEBUG_LEVEL'
];
const expectedKeysNew = [
  'nuvoton_firmware',
  'nuvoton_chip_id',
  'serial',
  'sensor',
  'battery_voltage',
  'usb_voltage',
  'usb_charging',
];

interface Port {
  path: string;
  manufacturer: string | undefined;
  serialNumber: string | undefined;
  pnpId: string | undefined;
  locationId: string | undefined;
  productId: string | undefined;
  vendorId: string | undefined;
}


@Injectable({
  providedIn: 'root'
})
export class ElectronUsbApiService {
  private oldSerialRegEx = '[45]W[0-9A-F]{18}';
  private isUSB = false;
  private firstConnection = false;
  private isEnabled: boolean;
  private port: Port;
  private vid: string;
  private serialNumber: string;
  private version = 1;
  private isRequesting: boolean;
  private baudRate: number;
  private readBytes = 0;
  private addSleep = false;
  private baudRates: number[] = [2000000, 500000];
  private resultNew: Uint8Array = new Uint8Array();
  private resultStr = '';
  private offset = 0;
  private maxLength = 2000000;
  private resultReady = false;
  private command: string;
  private init = false;

  constructor(
  ) {
    this.initCallback();
  }

  initCallback() {
    if (!this.init && window && !!window.electronAPI) {
      window.electronAPI.onSerialData(this.handleData.bind(this));
      this.resultNew = new Uint8Array(this.maxLength);
      this.init = true;
    }
  }

  resetCmd() {
    this.resultNew.fill(0);
    this.offset = 0;
    this.resultStr = '';
    this.resultReady = false;
  }

  getResult() {
    return this.resultNew.slice(0, this.offset);
  }

  async awaitSleep(ms = 250) {
    if (!this.addSleep) {
      await new Promise(r => setTimeout(r, ms));
    } else {
      await new Promise(r => setTimeout(r, ms + 500));
    }
  }

  async requestPermission(
    state: StateInterface,
    setState: ((key: State, val: any) => void),
    setBlockScreen: ((block: boolean) => void) = (() => { })
  ): Promise<[string, number]> {
    this.initCallback();

    const isOpen = await window.electronAPI.status();
    if (isOpen && this.serialNumber && this.version) {
      return [this.serialNumber, this.version];
    }
    setBlockScreen(true);
    this.serialNumber = undefined;

    this.isEnabled = true;
    const ports = await window.electronAPI.listPorts();
    if (ports.length > 0) {
      this.port = ports[0];
      this.vid = this.port.vendorId;
    }
    if (!this.vid) {
      setBlockScreen(false);
      return ['', 0];
    }
    this.addSleep = ((this.vid === '10c4' || this.vid === '0416') && navigator.userAgent.includes('Windows'));
    await this.openPort(setState);
    this.firstConnection = true;
    setBlockScreen(false);
    return [this.serialNumber, this.version];
  }

  async openPort(setState: ((key: State, val: any) => void)) {
    if (!this.vid) {
      return;
    }
    const statusPort = await window.electronAPI.status();
    if (statusPort && this.serialNumber !== undefined) {
      await this.enableUSB();
      return this.serialNumber;
    }

    try {
      await window.electronAPI.closePort();
    } catch (e) { console.log(e); return this.serialNumber; }

    this.serialNumber = undefined;
    this.version = 1;
    const isWindows = navigator.userAgent.includes('Windows');
    for (const baudRate of this.baudRates) {
      try {
        await window.electronAPI.openPort({path: this.port.path, baudRate});
        await this.awaitSleep();
        if (isWindows) {
          await window.electronAPI.setSignals({dtr: true, rts: true});
          await this.awaitSleep();
          await window.electronAPI.setSignals({dtr: false, rts: true});
          await this.awaitSleep();
          await window.electronAPI.setSignals({dtr: false, rts: false});
          await this.awaitSleep();
        } else {
          await window.electronAPI.setSignals({dtr: false, rts: false});
        }
        await this.awaitSleep(2000);
      } catch (e) {
        console.log(e);
        continue;
      }
      this.isUSB = false;

      try {
        // TODO
        // if (this.vid !== '04B4') {
        await this.enableUSB();
        // }
        const message = await this.executeCommand('I');
        if (!message || message === '' ||
          !(['4W', '5W', '1E', '1N', '2E', '2N'].includes(message.slice(0, 2)))
        ) {
          await this.executeCommand('\n');
        } else {
          const ind = this.baudRates.indexOf(baudRate);
          this.baudRates.splice(ind, 1);
          this.baudRates.unshift(baudRate);
          this.baudRate = baudRate;
          this.serialNumber = message;
          if (['1N', '2N'].includes(message.slice(0, 2))) {
            const status = await this.executeCommand('S');
            if (status !== '') {
              try {
                const statusJSON = JSON.parse(status);
                setState(State.SCANNER_STATUS, statusJSON);
              } catch (e) {
                //
              }
            }
          }
          break;
        }
      } catch (e) { }
      let rev2Status = await this.executeCommand2('get_status\n', true);
      if (rev2Status === '') {
        rev2Status = await this.executeCommand2('status\n', true);
      } else if (rev2Status.includes('error')) {
        rev2Status = await this.executeCommand2('status\n', true);
      }
      if (rev2Status !== '') {
        try {
          const rev2JSON = JSON.parse(rev2Status);
          setState(State.SCANNER_STATUS, rev2JSON);
          if (rev2JSON.command) {
            this.version = 2;
          }
          const ind = this.baudRates.indexOf(baudRate);
          this.baudRates.splice(ind, 1);
          this.baudRates.unshift(baudRate);
          this.baudRate = baudRate;
          this.serialNumber = rev2JSON.serial;
          break;
        }
        catch (e) { }
      } else {
      }
      const statusPort2 = await window.electronAPI.status();
      if (statusPort2) {
        try {
          await window.electronAPI.closePort();
        } catch (e) { }
      }
    }
    return this.serialNumber;
  }

  async handleData(data: Uint8Array) {
    // this.lastRead = Date.now();
    while (data.length > 0 && data[0] === 128 && this.version === 2) {
      data = data.slice(1);
    }
    const offset = this.offset;
    this.offset += data.length;
    this.resultNew.set(data, offset);
    if (this.command === 'M') {
      return;
    }
    const arrStr = await new Response(this.getResult().buffer).text();

    if (arrStr.includes('\n')) {
      this.resultStr = arrStr;
      this.resultReady = true;
    }
  }

  async disableUSB() {
    if (!this.isUSB) { return; }
    await this.executeCommand('X\n');
    this.isUSB = false;
  }

  async enableUSB(firstConnection = false) {
    if (this.version === 2) {
      return;
    }
    if (this.isUSB) { return; }
    if (!((this.serialNumber ?? '').startsWith('4W')) && !((this.serialNumber ?? '').startsWith('5W')) && !firstConnection) {
      await this.executeCommand('X\n');
      await this.executeCommand('start_usb\n');
      await new Promise((r) => setTimeout(r, 500));
    }
    this.isUSB = true;
  }

  async prepareConnection(isUSB = true, forceRestart = false) {
    if (isUSB && !this.isUSB) {
      if (!((this.serialNumber ?? '').startsWith('4W')) && !((this.serialNumber ?? '').startsWith('5W'))) {
        await this.executeCommand('X\n');
        await this.executeCommand('start_usb\n');
        this.isUSB = true;
      }
    } else if (!isUSB && this.isUSB) {
      await this.executeCommand('X\n');
      this.isUSB = false;
    }
  }

  bufferToHex(buf) {
    return [...new Uint8Array(buf)]
      .map(b => b.toString(16).padStart(2, '0'))
      .join('')
      .toUpperCase();
  }

  async executeCommand(command: string) {
    this.command = command;
    this.resetCmd();
    const ts = Date.now();
    await window.electronAPI.write({command});
    if (command === 'M') {
      return '';
    }

    let timeoutVal = 500;
    let expectedBytes = 1000;


    switch (command) {
      case 'get_status\n': timeoutVal = 1000; expectedBytes = 600; break;
      case 'W': timeoutVal = 1000; expectedBytes = 44; break;
      case 'I': timeoutVal = 200; expectedBytes = 28; break;
      case 'start_usb\n':
      case 'X\n': timeoutVal = 10; expectedBytes = 0; break;
      default: timeoutVal = 100; break;
    }
    while (!this.resultReady) {
      await new Promise((r) => setTimeout(r, 1));
      if (
        ((Date.now() - ts) >= timeoutVal
          || this.offset >= expectedBytes)
        && (command !== 'get_status\n' || this.resultStr !== '')
      ) {
        break;
      }
    }
    const messageArray = this.getResult();
    if (messageArray.length === 0) {
      return '';
    }
    let result: string;
    switch (command) {
      case 'I': {
        if (messageArray.length < 28) {
          return '';
        }
        result = (new TextDecoder('utf-8')).decode(messageArray.slice(2, 2 + 6));
        result += this.bufferToHex(messageArray.slice(2 + 6, 2 + 13));
        break;
      }
      case 'S':
      case 'get_status\n': {
        const buffer = messageArray.buffer;
        const r = await new Response(buffer).json();
        result = JSON.stringify(r);
        break;
      }
      case 'W': {
        const dv = new DataView(messageArray.buffer);
        const dark = dv.getFloat32(2, true);
        const bright = dv.getFloat32(2 + 2 * 4, true);
        result = JSON.stringify([dark, bright]);
        break;
      }
      default: {
        break;
      }
    }
    await new Promise((r) => setTimeout(r, 100));
    return result;
  }

  /**
   * Sends a generic command with a payload, ensuring the connection is prepared first.
   * This handles switching the device to the correct USB mode if necessary.
   * @param command - The command string to send (e.g., 'M', 'S', etc.).
   * @param receivedData - The additional data (object, string, or Buffer).
   * @returns A promise that resolves with the serial write result.
   */
  async executeGenericCommand(command: string, receivedData: any) {
    await this.prepareConnection(true);
    return await this.executeCommandWithPayload(command, receivedData);
  }

  /**
   * Sends a command along with an additional object payload to the serial port.
   * If the payload is an object, it will be converted to a JSON string.
   * @param command - The command string to send.
   * @param receivedData - The additional data (object, string, or Buffer).
   * @returns A promise that resolves with the serial write result (e.g., 'command-sent').
   * @throws An error if the operation fails.
   */
  async executeCommandWithPayload(command: string, receivedData: any) {
    try {
      return await window.electronAPI.writePayload({command, receivedData});
    } catch (error) {
      console.error('Error sending command with payload:', error);
      throw error;
    }
  }

  async getStatus(state: StateInterface): Promise<ScannerStatus> {
    if (this.version === 2) {
      return this.getStatus2(state);
    }
    await this.prepareConnection(false);
    const res = await this.executeCommand('get_status\n');
    if (res && res.length > 0) {
      try {
        const jsonString = res.replace('\r\n', '');
        const json = JSON.parse(jsonString);
        const keys = [];
        for (const key of Object.keys(json)) {
          keys.push(key);
        }
        if (keys.includes('capabilities')) {
          for (const key of Object.keys(json.capabilities)) {
            keys.push(`capabilities.${key}`);
          }
        }
        for (const key of ((state.SCANNER_SERIAL ?? '').startsWith('1N') ? expectedKeysNew : expectedKeys)) {
          if (!(keys.includes(key))) {
            return;
          }
        }
        return json;
      } catch (e) {
        return;
      }
    }
    return;
  }

  /**
   * Performs hand recognition based on the given settings.
   * @param settings - The current settings for hand recognition.
   * @returns An Observable that emits a boolean value indicating if hand recognition was successful.
   */
  async doHandrecognitionObservable(state: StateInterface, settings: CurrentSettings, handOff = false): Promise<boolean> {
    if (this.version === 2) {
      return this.doHandrecognitionObservable2(state, settings, handOff);
    }
    await this.prepareConnection(true);
    const res = await this.executeCommand('W');
    try {
      let result;
      const resJson = JSON.parse(res);
      const dark: number = resJson[0];
      const bright: number = resJson[1];
      if (this.addSleep) {
        await new Promise(r => setTimeout(r, 500));
      }
      if (bright < 0.0001) {
        return false;
      }
      if (state.SCANNER_SERIAL && state.SCANNER_SERIAL.match(this.oldSerialRegEx)) {
        result = (dark < 2) && ((bright > 500) && (bright < 6000));
      } else {
        const darkUpper = settings.handRecognitionLimitDarkUpper;
        const brightLower = settings.handRecognitionLimitBrightLower;
        const brightUpper = settings.handRecognitionLimitBrightUpper;
        result = (dark < darkUpper) && ((bright > brightLower) && (bright < brightUpper));
      }
      if (handOff) {
        result = !result;
      }
      return result;
    } catch (e) { return false; }
  }

  doAOXMeasurementObservable(measurementType: string): Observable<HttpEvent<any>> {
    return this.readMeasurement(measurementType);
  }

  readMeasurement(measurementType: string) {
    if (this.version === 2) {
      return this.readMeasurement2(measurementType);
    }
    const timeoutVal = 250;
    let expectedBytes = 190509;

    if (['4W', '5W'].includes(this.serialNumber.slice(0, 2))) {
      expectedBytes = 73845;
    }

    const initFunc = async () => {
      await this.executeCommand('M');
      return;
    };

    return from(initFunc()).pipe(
      switchMap(async () => {
        let isDone = false;
        if (this.offset >= expectedBytes) {
          isDone = true;
        }
        if (isDone) {
          const buffer = this.getResult().buffer;
          const r = Buffer.from(buffer).toString('base64');
          await new Promise(r => setTimeout(r, 500));
          return new HttpResponse({ body: r });
        }
        return { type: HttpEventType.DownloadProgress, loaded: (this.offset), total: expectedBytes } as HttpProgressEvent;
      }),
      delay(2),
      timeout(2500),
      repeat(),
      catchError((err) => {
        console.log(err);
        return of(new HttpResponse({ body: '' }));
      })
    );
  }







  async read2(command: string = '', id: string = '') {
    let timeoutDur = 2000;
    if (command.startsWith('read-flash')) {
      timeoutDur = 5000;
    } else if (command.startsWith('get_status')) {
      timeoutDur = 2000;
    } else if (command.startsWith('delete')) {
      timeoutDur = 10000;
    }

    const ts = Date.now();
    while (!this.resultReady) {
      await new Promise((r) => setTimeout(r, 1));
      if ((Date.now() - ts) >= timeoutDur) {
        break;
      }
    }
    const buffer = this.getResult().buffer;
    const r1 = await new Response(buffer).text();
    if (!r1.includes('\n')) {
      return '';
    }
    const dataSplit = r1.split('\n');

    if (command.startsWith('read-flash')) {
      return r1;
    }
    for (const data of dataSplit) {
      try {
        const r = JSON.parse(data);
        if (r.command === undefined) {
          continue;
        }
        if (r?.['call-index'] !== undefined && r?.['call-index'] && id !== '') {
          if (r?.['call-index'] !== id) {
            continue;
          }
        }
        return data;
      } catch (e) {

      }
    }
    return '';
  }

  async write2(message: string, init = false) {
    const id = uuid.v4().slice(0, 8);
    let commandAdjusted = message;
    if (!init) {
      if (commandAdjusted.includes('?')) {
        commandAdjusted += '&';
      } else {
        commandAdjusted += '?';
      }
      commandAdjusted += `call-index=${id}\n`;
    }
    try {
      await window.electronAPI.write({command: commandAdjusted});
    } catch (e) {
      return ['', id];
    }
    return [commandAdjusted, id];
  }

  async executeCommand2(command: string, init = false) {
    this.resetCmd();
    const [commandAdjusted, id] = await this.write2(command, init);
    const messageString = await this.read2(commandAdjusted, id);
    return messageString;
  }

  async getStatus2(state: StateInterface): Promise<ScannerStatus> {
    let res;
    if (state.SCANNER_SERIAL.startsWith('2N')) {
      res = await this.executeCommand2('status');
    } else {
      res = await this.executeCommand2('get_status');
    }
    try {
      const json = JSON.parse(res);
      return json;
    } catch (e) {
      return;
    }
    return;
  }


  async doHandrecognitionObservable2(state: StateInterface, settings: CurrentSettings, handOff = false): Promise<boolean> {
    const firmware: string = state?.SCANNER_STATUS?.wifi_firmware ?? '';
    const firmwareNuv: string = state?.SCANNER_STATUS?.nuvoton_firmware ?? '';
    const vSplit = firmware.split('.');
    const vSplitNuv = firmwareNuv.split('.');
    if (vSplit.length > 2 || vSplitNuv.length > 2) {
      const v0 = Number(vSplit[0]);
      const v1 = Number(vSplit[1]);
      const v2 = Number(vSplit[2]);
      const vNuv1 = Number(vSplitNuv[1]);
      if ((v2 > 19 || v1 > 0 || ![10, 20, 0].includes(v0) ) || (vNuv1 >= 4)) {
        const start = handOff ? 134 : 110;
        const count = handOff ? 10 : 13;
        const cycles = handOff ? 25 : 5;

        const resInner = await this.executeCommand2(`hand?start=${start}&count=${count}&cycles=${cycles}`);
        try {
          const jsonParsed = JSON.parse(resInner);
          const result = parseHandRecognition(jsonParsed.data, handOff, start, count, cycles);
          if (result) {
            const dataToSend = [];
            for (let i = 0; i < jsonParsed.data.length; i++) {
              if (((i + 1) % (cycles + 2)) === 0) {
                continue;
              }
              dataToSend.push(jsonParsed.data[i]);
            }
            state.HAND_RECOGNITION_DATA = dataToSend;
          }
          return result;
        } catch (e) {
          return false;
        }
      }
    }

    const darkUpper = settings.handRecognitionLimitDarkUpper;
    const brightLower = settings.handRecognitionLimitBrightLower;
    const brightUpper = settings.handRecognitionLimitBrightUpper;

    const res = await this.executeCommand2(`hand?du=${darkUpper}&bl=${brightLower}&bu=${brightUpper}`);

    try {
      let result = JSON.parse(res).hand === 0;
      if (handOff) {
        result = !result;
      }
      return result;
    } catch (e) {
      return false;
    }
  }

  readMeasurement2(measurementType: string) {

    let totalBlocks = 5773;
    let command = 'measure?type=aox-prod';
    let resultIndex = 0;
    switch (measurementType) {
      case 'full': {
        totalBlocks = 5773;
        command = 'measure?type=aox';
        break;
      }
      case 'front_half': {
        totalBlocks = 2887;
        command = 'measure?type=aox-half';
        break;
      }
      case 'hrv': {
        totalBlocks = 45000;
        command = 'measure?type=hrv';
        break;
      }
      case 'hrv-test': {
        totalBlocks = 5000;
        command = 'measure?type=hrv-test';
        break;
      }
      default: {
        totalBlocks = 5773;
        command = 'measure?type=aox';
        break;
      }
    }
    const isHRV = measurementType.startsWith('hrv');
    const foundIndexes = Array(totalBlocks).fill(false);
    const resDict = {};
    let missingArray = [[0, 150]];
    let goodBlocks = 0;
    let startTS: number;
    let command2: string;
    let id2: string;
    const initFunc = async () => {
      if (resultIndex > 0) {
        return;
      }
      await new Promise((r) => setTimeout(r, 1000));
      this.resetCmd();
      const [command1, id1] = await this.write2('delete');
      await this.read2(command1, id1);
      this.resetCmd();
      [command2, id2] = await this.write2(command);
      await new Promise((r => setTimeout(r, 250)));
      return;
    };

    return from(initFunc()).pipe(
      switchMap(async _ => {
        if (resultIndex > 0) {
          return resultIndex;
        }
        const resultRaw = await this.read2(command2, id2);
        startTS = Date.now();
        try {
          const resultJson = JSON.parse(resultRaw);
          resultIndex = resultJson.measurement_nr;
          return resultIndex;
        } catch (e) {
          return 0;
        }
        // return 60;
      }),
      delay(100),
      switchMap(async _ => {

        if (goodBlocks === totalBlocks) {
          return new HttpResponse({ body: missingArray.join('') });
        }

        const currentTS = Date.now();
        const blocksPerSecond = isHRV ? 400 : 400;
        const currentEntry = missingArray[0];
        const currentMax = Math.floor(((currentTS - startTS) / 1000) * blocksPerSecond);

        const start = currentEntry[0];
        let range = currentEntry[1];

        if (currentMax <= start) {
          return { type: HttpEventType.DownloadProgress, loaded: (goodBlocks), total: totalBlocks } as HttpProgressEvent;
        }



        if (start + range > currentMax) {
          range = currentMax - start + 1;
        }
        const r = await this.executeCommand2(`read-flash?nr=${resultIndex}&block=${start}&range=${range}`);
        if (r !== '') {
          const { status: statusEval, data: d, goodBlocks: gb, ok } = evalJsonWrapper(r, resDict, totalBlocks, foundIndexes, isHRV);
          if (ok) {
            goodBlocks = gb;
            missingArray = d;
          }
          if (statusEval) {
            return new HttpResponse({ body: missingArray.join('') });
          }
        }
        return { type: HttpEventType.DownloadProgress, loaded: (goodBlocks), total: totalBlocks } as HttpProgressEvent;
      }),
      delay(100),
      repeat(),
    );
  }

  async finalizeScanner(pipelineConfig: PipelineStageConfig) {
    const productGroup = pipelineConfig.state?.[pipelineConfig?.stageConfig?.resGroup] ?? {};
    const product = getKeyVal(pipelineConfig?.stageConfig?.resKey, productGroup);

    const wifiGroup = pipelineConfig.state?.[pipelineConfig?.stageConfig?.getGroup] ?? {};
    const wifiPrefix = getKeyVal(pipelineConfig?.stageConfig?.getKey, wifiGroup);

    const timestampString = new Date()
      .toISOString()
      .replace(/[-:TZ.]/g, '') // remove separators and timezone
      .slice(0, 14); // keep only YYYYMMDDHHMMSS

    const [command1, id1] = await this.write2(`product?product=${product}&wifi_prefix=${wifiPrefix}&production=${timestampString}`);
    await this.read2(command1, id1);
    await new Promise((r => setTimeout(r, 250)));
    const [command2, id2] = await this.write2(`mode?set=user`);
    await this.read2(command2, id2);
    await new Promise((r => setTimeout(r, 250)));

    return true;
  }
}
